/**
 * "Dock Received" page - view incoming POs and Transshipments
 */

import React, {
    useRef,
    useState,
    useEffect,
    useMemo,
} from "react"
import PropTypes from "prop-types"

import Column from "@amzn/meridian/column"
import Heading from "@amzn/meridian/heading"
import Row from "@amzn/meridian/row"



import PageLayout from "../../components/app/page-layout"
import NetworkLoadSlaTable from "../../components/network-load-sla/network-load-sla-table"
import TableFooter from "../../components/app/table-footer"
import NetworkLoadSlaEmpty from "../../components/network-load-sla/network-load-sla-empty"
import NetworkLoadSlaActionBar from "../../components/network-load-sla/network-load-sla-action-bar"
import usePagination from "../../hooks/use-pagination"
import useNetworkSLAFilters from "../../hooks/use-network-sla-filters"
import usePageLayoutContentRectangle from "../../hooks/use-page-layout-content-rectangle"
import dockReceivedPropType from "../../prop-types/dock-received"
import { resultsPerPageOptions, slaShipmentProgressThreshold } from "../../components/Constants";
import { getSummaryQuantity } from "../../utilities/parent-child-conversion";
import useFilters from "../../hooks/use-filters";
import filterNetworkSLA from "../../filters/filter-network-sla";


const getTableWidth = wrapperElement => {
    try {
        return wrapperElement.querySelector("table").clientWidth
    } catch (_) {
        return undefined
    }
}

const NetworkLoadSla = props => {
    const { incomingDockReceived } = props
    const { filters } = useNetworkSLAFilters()
    const { width: contentWidth = 1200 } = usePageLayoutContentRectangle()
    const tableWrapperRef = useRef()
    const [resultsPerPage, setResultsPerPage] = useState(10)
    const [selectedResultsPerPage, setSelectedResultsPerPage] = useState(10)

    const filteredDockReceived = useFilters(incomingDockReceived, filterNetworkSLA, filters)

    // Filter to only show shipments that are actually past their SLA deadline
    const dockReceived = useMemo(() => {
        if (!filteredDockReceived) return filteredDockReceived

        return filteredDockReceived.filter(shipment => {
            // Only include shipments that are past SLA using the same logic as dock received view
            if (shipment.progress >= slaShipmentProgressThreshold) {
                return false // Skip completed shipments
            }

            const slaDeadline = shipment.lastDockReceiveTime +
                ((shipment.sla ? Math.min(shipment.sla, 24) : 24) * 60 * 60 * 1000)

            return Date.now() > slaDeadline // Only include if past SLA deadline
        })
    }, [filteredDockReceived])

    // Memoize expensive total calculations to avoid recalculation on every render
    // Use the same precise logic as the NYR/NYS Summary to only count NYR units for shipments actually past SLA
    const totals = useMemo(() => {
        let totalNYRPastSLA = 0;

        if (filteredDockReceived) {
            filteredDockReceived.forEach(shipment => {
                // Only count NYR units for shipments that are past SLA using the same logic as summary view
                if (shipment.progress < slaShipmentProgressThreshold) {
                    const slaDeadline = shipment.lastDockReceiveTime +
                        ((shipment.sla ? Math.min(shipment.sla, 24) : 24) * 60 * 60 * 1000);

                    if (Date.now() > slaDeadline) {
                        // Use shipment's calculated NYR value (item-level aggregation)
                        const nyrPastSLA = Math.max(getSummaryQuantity(shipment, 'nyrnys', 'childNyrnys'), 0);
                        totalNYRPastSLA += nyrPastSLA;
                    }
                }
            });
        }

        return { totalNYR: totalNYRPastSLA };
    }, [filteredDockReceived]);

    const pagination = usePagination({
        numberOfItemsPerPage: resultsPerPage,
        numberOfResults: dockReceived?.length || 0,
    })
    const tableWidth = getTableWidth(tableWrapperRef.current)
    const isSticky = contentWidth < tableWidth

    const { visibleIndices } = pagination

    const onChangeResultsPerPage = resultsPerPage => {
        // Update local state variable
        setSelectedResultsPerPage(resultsPerPage)
        if (resultsPerPage === "All") {
            setResultsPerPage(dockReceived?.length || 0)
        } else {
            setResultsPerPage(resultsPerPage)
        }
        // Change pagination back to page 1. This avoids the weird state users can
        // find themselves in where they're on a page that no longer exists if the
        // number of pages decreases.
        pagination.onChange(1)
    }

    // Set pagination back to page 1 when filters change
    useEffect(() => {
        // Change pagination back to page 1. This avoids the weird state users can
        // find themselves in where they're on a page that no longer exists if the
        // number of pages decreases.
        onChangeResultsPerPage(selectedResultsPerPage)
        // We disable the exhaustive-deps hooks rule here because including `pagination`
        // will cause the effect to be called over-and-over.
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [filters, dockReceived])


    return (
        <PageLayout
            loading={!dockReceived}
            width={dockReceived ? "inherit" : undefined}
            horizontallySticky={isSticky}
            maxWidth={isSticky ? "none" : undefined}
            alignmentHorizontal={isSticky ? "start" : "center"}
        >
            <Column spacing="300">
                <Column>
                    <Row><Heading level={2}>Loads Past SLA</Heading></Row>
                    <Row>
                        <NetworkLoadSlaActionBar
                            stick={isSticky}
                            dockReceived={dockReceived}
                            totalNYR={totals.totalNYR}
                        />
                    </Row>
                    <Row
                        spacingInset={isSticky ? "none 500 none none" : undefined}
                        ref={tableWrapperRef}
                    >

                        <NetworkLoadSlaTable
                            dockReceived={dockReceived}
                            visibleIndices={visibleIndices}
                        />
                    </Row>

                    {dockReceived && dockReceived.length === 0 ? (
                        <Row><NetworkLoadSlaEmpty searching={Boolean(filters.size)} /></Row>
                    ) : null}
                    {dockReceived && dockReceived.length > 0 ? (
                        <Row>
                            <TableFooter
                                pagination={pagination}
                                stick={false}
                                resultsPerPage={resultsPerPage}
                                onChangeResultsPerPage={onChangeResultsPerPage}
                                resultsPerPageOptions={resultsPerPageOptions}
                                selectedResultsPerPage={selectedResultsPerPage}
                            />
                        </Row>
                    ) : null
                    }
                </Column>
            </Column>
        </PageLayout>
    )
}

NetworkLoadSla.propTypes = {
    incomingDockReceived: PropTypes.arrayOf(dockReceivedPropType),
}

export default NetworkLoadSla
